/** Общие примитивы интерфейса.
 *
 * ЗАЧЕМ. Аудит показал: чип реализован восемью способами (два радиуса, три
 * высоты, семь паддингов), первичная кнопка — двадцатью, поле ввода —
 * одиннадцатью. Само по себе повторение терпимо; беда в том, что копии
 * РАЗОШЛИСЬ, и на одном экране рядом стоят чипы 34 и 38 пикселей высотой.
 *
 * Здесь нет «универсальных» пропсов на все случаи: каждый примитив закрывает
 * ровно то, что встречалось в коде. Понадобится вариант — добавится вариант, а
 * не булев флаг, из которого потом собирают четыре несовместимые формы.
 */
import type React from "react";
import { tap } from "../lib/telegram.ts";

/** Корневой отступ экрана. Единый: три разных значения дёргали содержимое при
 *  переходе с вкладки на вложенную страницу. */
export const screen: React.CSSProperties = { padding: "var(--screen)" };

/** Карточка-поверхность. Объявлялась идентично в одиннадцати файлах. */
export const card: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--r-lg)",
  padding: "var(--s-5)",
};

/** Карточка-контейнер списка: строки несут свои отступы сами. */
export const listCard: React.CSSProperties = {
  ...card,
  padding: "2px var(--s-5)",
};

/** Приглушённая подпись под блоком. Объявлялась идентично тринадцать раз. */
export const hint: React.CSSProperties = {
  fontSize: "var(--f-caption)",
  color: "var(--text-muted)",
  lineHeight: 1.55,
  marginTop: "var(--s-3)",
  marginBottom: 0,
};

/** Поле ввода. Высота из --pad-control даёт цель в 44px при кегле --f-body. */
export const field: React.CSSProperties = {
  width: "100%",
  minHeight: "var(--tap)",
  background: "var(--surface-2)",
  border: "none",
  borderRadius: "var(--r-field)",
  padding: "12px var(--pad-control)",
  color: "var(--text)",
  font: "inherit",
  fontSize: "var(--f-body)",
};

/** Первичное действие. `tone` — то, чем копии реально отличались. */
export function Button({ children, onPick, tone = "primary", disabled, grow, style }: {
  children: React.ReactNode;
  onPick: () => void;
  tone?: "primary" | "quiet" | "danger";
  disabled?: boolean;
  grow?: boolean;
  style?: React.CSSProperties;
}) {
  const paint =
    tone === "danger" ? { background: "var(--danger)", color: "var(--on-accent)" }
    : tone === "quiet" ? { background: "var(--surface-2)", color: "var(--text)" }
    : { background: "var(--accent)", color: "var(--on-accent)" };
  return (
    <button type="button" disabled={disabled} onClick={() => { tap(); onPick(); }}
      style={{
        width: grow ? undefined : "100%", flexGrow: grow ? 1 : undefined,
        minHeight: "var(--tap)", padding: "var(--pad-control)",
        borderRadius: "var(--r-field)", border: "none", cursor: "pointer",
        font: "inherit", fontSize: "var(--f-body)", fontWeight: 600,
        ...(disabled ? { background: "var(--surface-2)", color: "var(--text-disabled)" } : paint),
        ...style,
      }}>
      {children}
    </button>
  );
}

/** Чип-переключатель. Одна высота и один радиус на все восемь прежних мест:
 *  рядом стоящие чипы разной высоты читались как разные виды контролов. */
export function Chip({ children, active, onPick, disabled, grow, ink }: {
  children: React.ReactNode;
  active: boolean;
  onPick: () => void;
  disabled?: boolean;
  grow?: boolean;
  /** Точка цвета слева — легенда пирога. */
  ink?: string;
}) {
  return (
    <button type="button" role="radio" aria-checked={active} disabled={disabled}
      onClick={() => { tap(); onPick(); }}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        minHeight: 38, padding: "0 var(--pad-control)", flexGrow: grow ? 1 : undefined,
        justifyContent: grow ? "center" : undefined,
        borderRadius: "var(--r-pill)", cursor: "pointer", font: "inherit",
        fontSize: "var(--f-caption)", fontWeight: active ? 600 : 400,
        background: active ? "var(--accent)" : "var(--surface-2)",
        color: active ? "var(--on-accent)" : "var(--text)",
        border: "none",
      }}>
      {ink && <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%",
                                                background: ink, flexShrink: 0 }} />}
      {children}
    </button>
  );
}

/** Плашка-пометка: «своя», «вручную», «идёт», «починено». Пять копий с тремя
 *  разными фонами и радиусом 5, которого нет в шкале. */
export function Badge({ children, tone = "muted" }: {
  children: React.ReactNode;
  tone?: "muted" | "ok" | "warn";
}) {
  const paint =
    tone === "ok" ? { color: "var(--income)", bg: "var(--income)" }
    : tone === "warn" ? { color: "var(--warn)", bg: "var(--warn)" }
    : { color: "var(--text-muted)", bg: "var(--text-muted)" };
  return (
    <span style={{ fontSize: "var(--f-micro)", whiteSpace: "nowrap", flexShrink: 0,
                   borderRadius: "var(--r-badge)", padding: "2px 6px",
                   color: paint.color,
                   background: `color-mix(in srgb, ${paint.bg} 12%, transparent)` }}>
      {children}
    </span>
  );
}

/** Полоса прогресса. Пять копий с четырьмя разными верхними отступами. */
export function Bar({ share, ink, label }: {
  /** Доля 0..1. Больше единицы обрезается — переполнение рисует вызывающий. */
  share: number;
  ink: string;
  label?: string;
}) {
  return (
    <div role={label ? "img" : undefined} aria-label={label}
         style={{ height: 6, borderRadius: "var(--r-bar)", background: "var(--surface-3)",
                  marginTop: 9, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(Math.max(share, 0), 1) * 100}%`, height: "100%",
                    background: ink, borderRadius: "var(--r-bar)" }} />
    </div>
  );
}

/** Заголовок секции. Ритм один на все экраны — в «Операциях» он был подменён
 *  отрицательным маргином. */
export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ fontSize: "var(--f-t3)", fontWeight: 600,
                 margin: "var(--s-6) 0 var(--s-3)" }}>
      {children}
    </h2>
  );
}

/** Кнопка «+» в шапке экрана. Три посимвольные копии в «Целях», «Копилках» и
 *  «Подписках» — одинаковые вплоть до отрицательного маргина. */
export function AddButton({ label, onPick }: { label: string; onPick: () => void }) {
  return (
    <button type="button" onClick={() => { tap(); onPick(); }} aria-label={label}
            style={{ width: 44, height: 44, marginRight: -11, background: "none",
                     border: "none", cursor: "pointer", display: "flex",
                     alignItems: "center", justifyContent: "center" }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
           strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  );
}

/** Строка «подпись — значение» в шторке. Была реализована в четырёх файлах, и
 *  две копии красили подпись и значение НАОБОРОТ: где-то серым было имя поля,
 *  где-то само число. Имя поля вторично, значение первично — так и раскрашено.
 *  Цифры табличные: строки стоят колонкой и обязаны выравниваться. */
export function SheetRow({ label, value, last }: {
  label: string; value: string; last?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                  padding: "var(--pad-control) 0", minHeight: "var(--tap)",
                  borderBottom: last ? "none" : "1px solid var(--separator)" }}>
      <span style={{ flexGrow: 1, fontSize: "var(--f-body)",
                     color: "var(--text-muted)" }}>{label}</span>
      <span className="num" style={{ fontSize: "var(--f-body)" }}>{value}</span>
    </div>
  );
}
